import React, { useState, useRef, useEffect } from "react";

/**
 * General Purpose Custom Select Component
 * Can be used anywhere in the application
 * 
 * @param {Array} options - Array of { value, label, icon } objects
 * @param {String} value - Currently selected value
 * @param {Function} onChange - Callback when selection changes
 * @param {String} placeholder - Placeholder text
 * @param {String} className - Additional CSS classes
 * @param {Boolean} showIcons - Whether to show icons (default: false)
 */
const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  className = "",
  showIcons = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  const getSelectedOption = () => {
    return options.find((opt) => opt.value === value) || null;
  };

  const selectedOption = getSelectedOption();

  return (
    <div className={`custom-select-wrapper ${className}`} ref={dropdownRef}>
      {/* Select Toggle Button */}
      <div
        className="custom-select-toggle"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex align-items-center flex-grow-1">
          {/* Selected Option Icon (if enabled) */}
          {showIcons && selectedOption?.icon && (
            <i className={`${selectedOption.icon} mr-2`}></i>
          )}
          
          {/* Selected Option Label */}
          <span className="custom-select-text">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>

        {/* Dropdown Arrow */}
        <i
          className={`mdi mdi-chevron-down custom-select-arrow ${
            isOpen ? "open" : ""
          }`}
        ></i>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="custom-select-menu">
          {options.map((option) => (
            <div
              key={option.value}
              className={`custom-select-item ${
                value === option.value ? "active" : ""
              }`}
              onClick={() => handleSelect(option.value)}
            >
              {/* Option Icon (if enabled) */}
              {showIcons && option.icon && (
                <i className={`${option.icon} mr-2`}></i>
              )}
              
              {/* Option Label */}
              <span className="custom-select-text">{option.label}</span>
              
              {/* Check mark for selected item */}
              {value === option.value && (
                <i className="mdi mdi-check text-primary ml-auto"></i>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;

